import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Autocomplete, Box, TextField } from "@mui/material";
import * as Yup from "yup";
import { toast } from "react-toastify";
import BootstrapTextField from "Common/BootstrapTextField";
import { useDispatch, useSelector } from "react-redux";
//thunk
import {
  fetchCategories,
  fetchCategoryByDropDown,
} from "slices/category/thunk";
import {
  fetchSubCategoryById,
  addSubCategory,
  updateSubCategory,
} from "slices/sub-category/thunk";
import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import PageContainer from "components/PageContainer/PageContainer";
import { autocompleteDropdownStyles } from "Constants/Style";

// Common input style for Form.Control to match Autocomplete styling
const commonInputStyle = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "13px",
};

const SubCategoryForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const categoryOptions = useSelector(
    (state: any) => state.Category.categoryDropdown
  );
  // const [categoryOptions, setCategoryOptions] = useState<any[]>([]);

  const [initialData, setInitialData] = useState({
    categories: [] as Array<{ category_id: string; category_name: string }>,
    subCategoryName: "",
    remark: "",
  });

  useEffect(() => {
    const fetchDropdown = async () => {
      try {
        const res = await dispatch(fetchCategoryByDropDown()).unwrap();
        // setCategoryOptions(res?.data || []);
      } catch {
        toast.error("Failed to load categories");
      }
    };

    const fetchSubCategoryData = async () => {
      if (id) {
        try {
          setIsLoading(true);
          const data = await dispatch(fetchSubCategoryById(id)).unwrap();

          setInitialData({
            categories:
              data?.categories || (data?.category ? [data.category] : []),
            subCategoryName: data.sub_category_name,
            remark: data.remark,
          });
        } catch {
          toast.error("Failed to load sub-category");
          // navigate("/sub-category");
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchDropdown();

    if (isEdit && id) {
      fetchSubCategoryData();
    }
  }, [dispatch, id, isEdit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      categoryIds: initialData.categories.map((c) => c.category_id),
      subCategoryName: initialData.subCategoryName,
      remark: initialData.remark,
    },

    validationSchema: Yup.object({
      categoryIds: Yup.array()
        .of(Yup.string())
        .min(1, "Please select at least one category")
        .required("Please select at least one category"),
      subCategoryName: Yup.string().required("Please enter sub-category name"),
      // remark: Yup.string().required("Please write remark"),
    }),
    onSubmit: async (values) => {
      const payload = {
        category_ids: values.categoryIds,
        sub_category_name: values.subCategoryName,
        remark: values.remark,
        // is_active: true,
      };
      console.log("Payload => ", payload);
      try {
        if (isEdit) {
          await dispatch(updateSubCategory({ id, data: payload })).unwrap();
          toast.success("Sub-category updated successfully");
        } else {
          await dispatch(addSubCategory(payload)).unwrap();
          toast.success("Sub-category added successfully");
        }
        navigate("/sub-category", { state: { clearState: !isEdit } });
      } catch (error: any) {
        console.error("Error during form submit:", error);
        toast.error(
          error?.message || "Something went wrong while saving sub-category"
        );
      }
    },
  });

  return (
    <PageContainer>
      <FormHeader
        title={isEdit ? "Edit Sub Category" : "Add Sub Category"}
        firstLabel="Sub Category"
        secondLabel={isEdit ? "Edit Sub Category" : "Add Sub Category"}
        handleArrowClick={() => navigate("/sub-category")}
      />

      {isEdit && isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <Form.Label>
                  Sub Category Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="subCategoryName"
                  placeholder="Enter Sub Category name"
                  value={formik.values.subCategoryName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.subCategoryName &&
                    formik.touched.subCategoryName
                  }
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.subCategoryName}
                </Form.Control.Feedback>
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <Form.Label>
                  Category <span className="text-danger">*</span>
                </Form.Label>

                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={categoryOptions}
                  getOptionLabel={(option: any) =>
                    typeof option === "string" ? option : option.category_name
                  }
                  value={categoryOptions.filter((c: any) =>
                    formik.values.categoryIds.includes(c.category_id)
                  )}
                  onChange={(event, selected) =>
                    formik.setFieldValue(
                      "categoryIds",
                      selected.map((s: any) => s.category_id)
                    )
                  }
                  onBlur={() => formik.setFieldTouched("categoryIds", true)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        {/* Option Label */}
                        <span>
                          {typeof option === "string"
                            ? option
                            : option.category_name}
                        </span>

                        {/* Show X only if option is selected */}
                        {selected && (
                          <span
                            style={{
                              cursor: "pointer",
                              marginLeft: "10px",
                              color: "#d32f2f",
                              fontWeight: "bold",
                            }}
                            onClick={(e) => {
                              e.stopPropagation(); // prevent normal selection toggling
                              const newValues =
                                formik.values.categoryIds.filter(
                                  (id) => id !== option.category_id
                                );
                              formik.setFieldValue("categoryIds", newValues);
                            }}
                          >
                            ✕
                          </span>
                        )}
                      </Box>
                    </li>
                  )}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Categories"
                      name="categoryIds"
                      error={
                        formik.touched.categoryIds &&
                        Boolean(formik.errors.categoryIds)
                      }
                      helperText={
                        formik.touched.categoryIds && formik.errors.categoryIds
                      }
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option: any, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Box
                          key={option.category_id}
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            backgroundColor: "#e3f2fd",
                            borderRadius: "3px",
                            padding: "2px 6px",
                            margin: "1px",
                            fontSize: "0.75rem",
                          }}
                          {...tagProps}
                        >
                          {option.category_name}
                        </Box>
                      );
                    })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>
          </Row>

          <Col lg={6}>
            <div className="mb-3">
              <Form.Label>Remark</Form.Label>
              <Form.Control
                type="text"
                name="remark"
                placeholder="Enter Remark"
                value={formik.values.remark}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.remark && formik.touched.remark}
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.remark}
              </Form.Control.Feedback>
            </div>
          </Col>

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton to="/sub-category" />

            {/* Reusable submit button with spinner */}
            <SubmitButton isSubmitting={formik.isSubmitting} isEdit={isEdit} />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default SubCategoryForm;
